import { z } from 'zod';
/**
 * The asset manifest (data/assets/manifest.json) maps logical keys to packed
 * atlases. Game code only ever references logical keys, never file paths
 * (CLAUDE.md rule 7), so an asset can be replaced without touching code.
 *
 * Paths are relative to the client's `assets/` URL, where `build:atlas` writes.
 */
export const AtlasDefinitionSchema = z.object({
    image: z.string().min(1),
    data: z.string().min(1),
    /**
     * Not loaded at boot: fetched when first needed (Shefira's clips, ADR 0067,
     * a few megabytes most players see twice in their whole journey).
     */
    lazy: z.boolean().optional(),
});
/** A single static frame (item icon, prop, UI element). */
export const SpriteEntrySchema = z.object({
    atlas: z.string().min(1),
    frame: z.string().min(1),
});
/**
 * An animated sheet (character body or equipment overlay). Frames are named
 * `<prefix>/<animation>/<direction>/<index>` inside the atlas so that every
 * paper-doll layer shares the same animation grid.
 */
export const SheetEntrySchema = z.object({
    atlas: z.string().min(1),
    prefix: z.string().min(1),
});
/**
 * A Wang tileset as a regular 4×4 grid image (16 tiles, index = corner mask
 * NW NE SW SE with 1 = grass), for the client's tilemap. The same tiles are
 * also packed in an atlas for anything that wants one by frame name.
 */
export const TilesetEntrySchema = z.object({
    image: z.string().min(1),
    tileSize: z.number().int().positive(),
});
/**
 * A sound (ADR 0019): the same audio in several formats, the most compact
 * first; the client plays the first one its browser can decode.
 */
export const SoundEntrySchema = z.object({
    files: z.array(z.string().min(1)).min(1),
});
export const AssetManifestSchema = z
    .object({
    version: z.literal(1),
    atlases: z.record(z.string().min(1), AtlasDefinitionSchema),
    sprites: z.record(z.string().min(1), SpriteEntrySchema),
    sheets: z.record(z.string().min(1), SheetEntrySchema),
    tilesets: z.record(z.string().min(1), TilesetEntrySchema).default({}),
    /** `sfx.<name>`, `music.<name>` and `ambience.<name>`, written by `build:atlas` from `data/audio`. */
    sounds: z.record(z.string().min(1), SoundEntrySchema).default({}),
})
    .superRefine((manifest, ctx) => {
    const checkAtlasReferences = (group, entries) => {
        for (const [key, entry] of Object.entries(entries)) {
            if (!(entry.atlas in manifest.atlases)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [group, key, 'atlas'],
                    message: `Unknown atlas "${entry.atlas}"`,
                });
            }
        }
    };
    checkAtlasReferences('sprites', manifest.sprites);
    checkAtlasReferences('sheets', manifest.sheets);
});
export const EMPTY_ASSET_MANIFEST = {
    version: 1,
    atlases: {},
    sprites: {},
    sheets: {},
    tilesets: {},
    sounds: {},
};
//# sourceMappingURL=asset-manifest.js.map